import PlusIcon from '@heroicons/react/24/outline/PlusIcon';
import TrashIcon from '@heroicons/react/24/outline/TrashIcon';
import { forwardRef } from 'react';

import { Button } from '@/components/core/Button';
import { ButtonPanel } from '@/components/core/ButtonPanel';
import { Loader } from '@/components/core/Loader';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/core/Table';
import { useAttributeLabelTranslator } from '@/components/datamodel/hooks/useAttributeLabelTranslator';
import { useFormatAttributeValue } from '@/components/datamodel/utils/formatAttributeValue';
import { useTranslation } from '@/hooks/useTranslation';
import type { DamLinkedItemColumns } from '@/pages/dam/utils/useDamLinkedItemColumns';
import type { DataModelAttribute, PersistedItem } from '@/types/DataModel';

interface DamLinkedArticlesPanelProps extends DamLinkedItemColumns {
    /** Linked items; `undefined` while loading. */
    items: PersistedItem[] | undefined;
    onAdd: () => void;
    onRemove: (item: PersistedItem) => void;
}

/**
 * "Linked Articles" of the detail page (wireframe `7037:48086`): "Add article", a table with
 * the columns of `asset_linked_items` (product name from its item title filter) and "Delete"
 * per row; empty state after Make. The table scrolls horizontally on narrow screens.
 */
const DamLinkedArticlesPanel = forwardRef<HTMLElement, DamLinkedArticlesPanelProps>(
    ({ items, columns, titleOf, onAdd, onRemove }, ref) => {
        const { t } = useTranslation();
        const { formatValue } = useFormatAttributeValue();
        const { translateAttributeLabel } = useAttributeLabelTranslator();

        const cellValue = (item: PersistedItem, column: DataModelAttribute) => {
            const display = formatValue(column, (item as unknown as Record<string, unknown>)[column.name]);
            return display && display !== '-' ? display : '–';
        };

        return (
            <section ref={ref} tabIndex={-1} className="tw-flex tw-flex-col tw-gap-4 tw-rounded-lg tw-border tw-border-solid tw-border-tertiary tw-bg-fill-primary tw-p-4 md:tw-p-6">
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                    <h2 className="tw-m-0 tw-text-t-base tw-font-bold tw-text-t-primary lg:tw-text-t-lg">{t('DAM.DETAIL.LINKED_ARTICLES')}</h2>
                    {items && items.length > 0 && (
                        <>
                            <Button variant="outline" hasIcon icon={PlusIcon} onClick={onAdd} className="tw-hidden md:tw-flex">
                                {t('DAM.DETAIL.ADD_ARTICLE')}
                            </Button>
                            <ButtonPanel
                                isDisabled={false}
                                icon={PlusIcon}
                                tooltip={t('DAM.DETAIL.ADD_ARTICLE')}
                                aria-label={t('DAM.DETAIL.ADD_ARTICLE')}
                                onClick={onAdd}
                                className="md:tw-hidden"
                            />
                        </>
                    )}
                </div>
                {items === undefined ? (
                    <div className="tw-flex tw-justify-center tw-py-4"><Loader /></div>
                ) : items.length === 0 ? (
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-rounded-lg tw-bg-fill-secondary tw-px-4 tw-py-8 tw-text-center">
                        <p className="tw-m-0 tw-text-t-base tw-font-bold tw-text-t-primary lg:tw-text-t-lg">{t('DAM.DETAIL.NO_ARTICLES_TITLE')}</p>
                        <p className="tw-m-0 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{t('DAM.DETAIL.NO_ARTICLES_TEXT')}</p>
                        <Button hasIcon icon={PlusIcon} onClick={onAdd} className="tw-mt-2">{t('DAM.DETAIL.ADD_ARTICLE')}</Button>
                    </div>
                ) : (
                    <Table containerClassName="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-solid tw-border-tertiary">
                        <TableHeader>
                            <TableRow>
                                {titleOf && <TableHead>{t('DAM.DETAIL.PRODUCT_NAME')}</TableHead>}
                                {columns.map((column) => <TableHead key={column.name}>{translateAttributeLabel(column)}</TableHead>)}
                                <TableHead className="tw-text-right">{t('DAM.DETAIL.ACTIONS')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow key={item.primaryKey__}>
                                    {titleOf && <TableCell className="tw-min-w-[12rem]">{titleOf(item)}</TableCell>}
                                    {columns.map((column) => (
                                        <TableCell key={column.name} className="tw-whitespace-nowrap">{cellValue(item, column)}</TableCell>
                                    ))}
                                    <TableCell className="tw-text-right">
                                        <Button
                                            variant="ghost"
                                            hasIcon
                                            icon={TrashIcon}
                                            onClick={() => onRemove(item)}
                                            className="tw-ml-auto tw-underline"
                                        >
                                            {t('DAM.DETAIL.DELETE')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </section>
        );
    },
);

DamLinkedArticlesPanel.displayName = 'DamLinkedArticlesPanel';

export { DamLinkedArticlesPanel };
export type { DamLinkedArticlesPanelProps };
